/**
 * A CHAVE DO RASCUNHO — quem é o dono daquilo que ficou guardado no navegador.
 *
 * 🔴 O QUE ESTAVA NO AR ATÉ AQUI (14/08). `Orcamentos.tsx` passava a chave
 * `"bancada-orcamento-novo"`: uma CONSTANTE, sem empresa e sem usuário. O
 * localStorage é do NAVEGADOR, não da empresa — então começar um orçamento na
 * M5, não terminar, trocar pro Átomo e abrir a bancada trazia o rascunho da M5
 * pra dentro do Átomo, com cliente, valores e itens.
 *
 * Não é vazamento entre pessoas (mesmo navegador, mesma pessoa) — é CONTEXTO
 * TROCADO, a mesma família do que já aconteceu com a Duda. O risco prático é
 * colar dado de uma empresa no orçamento de outra e mandar pro cliente.
 *
 * 🔑 O ESCOPO MORA AQUI, E NÃO NO CHAMADOR. Quem passa a chave esquece — foi
 * exatamente o que aconteceu. Quem chama o hook passa só a BASE ("qual
 * formulário é este"); empresa e usuário são costurados por dentro.
 */

/** Prefixo de TODO rascunho escopado. É por ele que se sabe se há trabalho aberto. */
export const PREFIXO_RASCUNHO = "rascunho:";

/**
 * `rascunho:<base>:<empresa>:<usuário>` — ou `null` quando ainda não dá pra
 * saber de quem é.
 *
 * O `null` é decisão, não descuido: sem empresa ativa (boot ainda carregando)
 * ou sem usuário, gravar produziria de novo um rascunho apátrida — o defeito
 * que este arquivo existe pra fechar. Melhor não guardar do que guardar sem
 * dono.
 */
export function chaveDoRascunho(a: {
  base: string;
  empresaId?: string | null;
  usuarioId?: string | null;
}): string | null {
  const base = (a.base ?? "").trim();
  const empresa = (a.empresaId ?? "").trim();
  const usuario = (a.usuarioId ?? "").trim();
  if (!base || !empresa || !usuario) return null;
  return `${PREFIXO_RASCUNHO}${base}:${empresa}:${usuario}`;
}

/**
 * DESCARTA o rascunho gravado com a chave antiga (sem escopo).
 *
 * Não MIGRA de propósito: a chave velha não diz de que empresa aquilo era. Uma
 * migração às cegas escolheria uma empresa no palpite e produziria exatamente o
 * estrago que estamos fechando — só que agora carimbado de "certo". Descartar
 * custa um rascunho de até 24h; adivinhar custa dado de uma empresa dentro de
 * outra.
 */
export function descartarRascunhoSemEscopo(base: string): boolean {
  try {
    if (localStorage.getItem(base) === null) return false;
    localStorage.removeItem(base);
    return true;
  } catch {
    return false; // localStorage bloqueado (aba anônima, quota) — nada a fazer
  }
}

/**
 * Havia trabalho digitado e não salvo no momento da queda de sessão?
 *
 * Usado pelo registro de queda (`lib/quedaDeSessao`): "caiu" é chato; "caiu com
 * um orçamento pela metade em cima" é a diferença entre incômodo e prejuízo, e
 * é o que decide a prioridade do conserto de verdade (o refresh).
 */
export function haRascunhoAberto(): boolean {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIXO_RASCUNHO)) return true;
    }
  } catch {
    /* storage indisponível → responde "não sei" como "não" */
  }
  return false;
}
